import React, { Suspense, lazy } from 'react';
import { Routes, Route, Navigate } from 'react-router-dom';
import ProtectedRoute from './ProtectedRoute';
import { useAuth } from './AuthContext';
import { TENANT_ROLES } from './types/roles';
import { Box, CircularProgress } from '@mui/material';

// Lazy imports
const LoginPage = lazy(() => import('./pages/LoginPage'));
const RegisterPage = lazy(() => import('./pages/RegisterPage'));
const ResetPasswordPage = lazy(() => import('./pages/ResetPasswordPage'));
const HomePage = lazy(() => import('./pages/HomePage'));
const UserManagementPage = lazy(() => import('./pages/UserManagementPage'));
const TenantManagementPage = lazy(() => import('./pages/TenantManagementPage'));
const TotemManagementPage = lazy(() => import('./pages/TotemManagementPage'));
const TicketManagementPage = lazy(() => import('./pages/TicketManagementPage'));
const DocumentTypeManagementPage = lazy(() => import('./pages/DocumentTypeManagementPage'));
const UserDocumentsPage = lazy(() => import('./pages/UserDocumentsPage'));
const UserEquipmentPage = lazy(() => import('./pages/UserEquipmentPage'));
const BarManagementPage = lazy(() => import('./pages/BarManagementPage'));
const RegistrationQrPage = lazy(() => import('./pages/RegistrationQrPage'));
const WebsiteWidgetPage = lazy(() => import('./pages/WebsiteWidgetPage'));
const PublicEventsWidgetPage = lazy(() => import('./pages/PublicEventsWidgetPage'));
const UserQualificationsPage = lazy(() => import('./pages/UserQualificationsPage'));
const EventManagementPage = lazy(() => import('./pages/EventManagementPage'));
const EventDetailPage = lazy(() => import('./pages/EventDetailPage'));
const FlightDetailPage = lazy(() => import('./pages/FlightDetailPage'));
const PublicTotemPage = lazy(() => import('./pages/PublicTotemPage'));
const PublicDocsPage = lazy(() => import('./pages/PublicDocsPage'));
const CustomerEventsPage = lazy(() => import('./pages/CustomerEventsPage'));
const UnderConstructionPage = lazy(() => import('./pages/UnderConstructionPage'));
const AdminRequestsPage = lazy(() => import('./pages/AdminRequestsPage'));
const DocumentApprovalsPage = lazy(() => import('./pages/DocumentApprovalsPage'));
const OperatorTenantSettingsPage = lazy(() => import('./pages/OperatorTenantSettingsPage'));
const UserProfilePage = lazy(() => import('./pages/UserProfilePage'));
const BoardingPassesPage = lazy(() => import('./pages/BoardingPassesPage'));
const AircraftManagementPage = lazy(() => import('./pages/AircraftManagementPage'));
const QualificationManagementPage = lazy(() => import('./pages/QualificationManagementPage'));
const JumpGroupRoleManagementPage = lazy(() => import('./pages/JumpGroupRoleManagementPage'));
const AdminLogsPage = lazy(() => import('./pages/AdminLogsPage')); // Import AdminLogsPage
const ShopPage = lazy(() => import('./pages/ShopPage'));
const CheckoutSuccessPage = lazy(() => import('./pages/CheckoutSuccessPage'));
const CheckoutCancelPage = lazy(() => import('./pages/CheckoutCancelPage'));
const UnauthorizedPage = lazy(() => import('./pages/UnauthorizedPage'));

const LoadingSpinner = () => (
  <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
    <CircularProgress />
  </Box>
);

const AppRoutes: React.FC = () => {
  const { loading, currentTenant } = useAuth();

  if (loading) {
    return <LoadingSpinner />;
  }

  const isCustomer = currentTenant?.role === 'customer' || currentTenant?.role === 'skydiver' || currentTenant?.role === 'student';
  // Il barista vede solo il bar: mandarlo su /events lo farebbe atterrare su una
  // pagina che non gli serve e che il backend gli nega quasi per intero.
  const homeRoute = currentTenant?.role === 'barman'
    ? (currentTenant.barEnabled ? '/admin/bar' : '/profile')
    : '/events';

  return (
    <Suspense fallback={<LoadingSpinner />}>
      <Routes>
        <Route path="/login" element={<LoginPage />} />
        <Route path="/register" element={<RegisterPage />} />
        <Route path="/reset-password" element={<ResetPasswordPage />} />
        <Route path="/unauthorized" element={<UnauthorizedPage />} />
        <Route path="/" element={<Navigate to={homeRoute} />} />
        <Route 
          path="/home" 
          element={
            <ProtectedRoute>
              <HomePage />
            </ProtectedRoute>
          } 
        />
        {/* Il profilo resta a tutti, barista compreso: password, tema, lingua. */}
        <Route 
          path="/profile" 
          element={
            <ProtectedRoute allowedRoles={[...TENANT_ROLES]}>
              <UserProfilePage />
            </ProtectedRoute>
          } 
        />
        <Route
          path="/admin/users"
          element={
            <ProtectedRoute allowedRoles={['admin', 'operator']}>
              <UserManagementPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/admin/tenants"
          element={
            <ProtectedRoute allowedRoles={['admin']}>
              <TenantManagementPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/admin/totems"
          element={
            <ProtectedRoute allowedRoles={['admin', 'operator']}>
              <TotemManagementPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/admin/tickets"
          element={
            <ProtectedRoute allowedRoles={['admin', 'operator']}>
              <TicketManagementPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/admin/document-types"
          element={
            <ProtectedRoute allowedRoles={['admin', 'operator']}>
              <DocumentTypeManagementPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/admin/requests"
          element={
            <ProtectedRoute allowedRoles={['admin', 'operator']}>
              <AdminRequestsPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/admin/approvals"
          element={
            <ProtectedRoute allowedRoles={['admin', 'operator']}>
              <DocumentApprovalsPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/admin/my-tenant"
          element={
            <ProtectedRoute allowedRoles={['operator']}>
              <OperatorTenantSettingsPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/admin/aircrafts"
          element={
            <ProtectedRoute allowedRoles={['admin', 'operator']}>
              <AircraftManagementPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/admin/qualifications"
          element={
            <ProtectedRoute allowedRoles={['admin', 'operator']}>
              <QualificationManagementPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/admin/jump-group-roles"
          element={
            <ProtectedRoute allowedRoles={['admin', 'operator']}>
              <JumpGroupRoleManagementPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/admin/logs"
          element={
            <ProtectedRoute allowedRoles={['admin']}>
              <AdminLogsPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/admin/users/:userId/documents"
          element={
            <ProtectedRoute allowedRoles={['admin', 'operator']}>
              <UserDocumentsPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/admin/users/:userId/boarding-passes"
          element={
            <ProtectedRoute allowedRoles={['admin', 'operator']}>
              <BoardingPassesPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/admin/registration-qr"
          element={
            <ProtectedRoute allowedRoles={['admin', 'operator']}>
              <RegistrationQrPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/admin/website-widget"
          element={
            <ProtectedRoute allowedRoles={['admin', 'operator']}>
              <WebsiteWidgetPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/admin/bar"
          element={
            <ProtectedRoute allowedRoles={['admin', 'operator', 'barman']}>
              <BarManagementPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/admin/users/:userId/equipment"
          element={
            <ProtectedRoute allowedRoles={['admin', 'operator']}>
              <UserEquipmentPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/admin/users/:userId/qualifications"
          element={
            <ProtectedRoute allowedRoles={['admin', 'operator']}>
              <UserQualificationsPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/my-documents"
          element={
            <ProtectedRoute>
              <UserDocumentsPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/my-equipment"
          element={
            <ProtectedRoute>
              <UserEquipmentPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/my-qualifications"
          element={
            <ProtectedRoute>
              <UserQualificationsPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/my-boarding-passes"
          element={
            <ProtectedRoute>
              <BoardingPassesPage />
            </ProtectedRoute>
          }
        />
        <Route 
          path="/events/:id" 
          element={
            <ProtectedRoute>
              <EventDetailPage />
            </ProtectedRoute>
          } 
        />
        <Route
          path="/events"
          element={
            <ProtectedRoute>
              {isCustomer ? <CustomerEventsPage /> : <EventManagementPage />}
            </ProtectedRoute>
          }
        />
        <Route
          path="/flights/:id"
          element={
            <ProtectedRoute>
              <FlightDetailPage />
            </ProtectedRoute>
          }
        />
        <Route path="/public/totem" element={<PublicTotemPage />} />
        <Route path="/public/docs" element={<PublicDocsPage />} />
        <Route path="/public/events" element={<PublicEventsWidgetPage />} />
        <Route
          path="/buy-tickets"
          element={
            <ProtectedRoute>
              <ShopPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/checkout/success"
          element={
            <ProtectedRoute>
              <CheckoutSuccessPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/checkout/cancel"
          element={
            <ProtectedRoute>
              <CheckoutCancelPage />
            </ProtectedRoute>
          }
        />
        <Route path="*" element={<Navigate to="/events" />} />
      </Routes>
    </Suspense>
  );
};

export default AppRoutes;
